/**
 * Campaign Permissions
 *
 * Pure helpers for clamping a peer's coordinator-layer `PeerPermissions` to the
 * active campaign's declared `supportedInputs` (from HostCampaignConfig).
 *
 * Semantics:
 * - `undefined` or empty `supportedInputs` means "unrestricted" — the campaign
 *   imposes no limit and the base permissions govern access. This preserves
 *   behavior for campaigns created before this field existed.
 * - When `supportedInputs` is a non-empty list, only the listed methods may be
 *   granted; any base permission for an unlisted method is forced off.
 *
 * Note: `PeerPermissions` has no `chat` field (chat is not gated by campaign
 * input support in the coordinator path).
 */

import type { PlayerInputType } from '@playcastdotio/Messaging';
import type { PeerPermissions } from './types';

/**
 * Default supported inputs when a campaign has no explicit `supportedInputs`
 * configured. Touch is excluded by default — campaigns must explicitly opt in.
 */
const DEFAULT_SUPPORTED_INPUTS: PlayerInputType[] = ['gamepad', 'keyboard', 'mouse'];

/**
 * Whether the campaign actively restricts inputs (a non-empty supported list).
 */
export const isCampaignInputRestricted = (
  supportedInputs: PlayerInputType[] | undefined,
): supportedInputs is PlayerInputType[] =>
  Array.isArray(supportedInputs) && supportedInputs.length > 0;

/**
 * Clamp a base permission set to the campaign's supported inputs. Campaigns
 * without an explicit `supportedInputs` array default to gamepad/keyboard/mouse
 * (touch must be explicitly enabled). Restricted campaigns force every unlisted
 * input off (never grants an input the base did not already allow).
 */
export const clampPeerPermissionsToCampaign = (
  base: PeerPermissions,
  supportedInputs: PlayerInputType[] | undefined,
): PeerPermissions => {
  const effective = isCampaignInputRestricted(supportedInputs)
    ? supportedInputs
    : DEFAULT_SUPPORTED_INPUTS;

  return {
    mouse: base.mouse && effective.includes('mouse'),
    keyboard: base.keyboard && effective.includes('keyboard'),
    touch: base.touch && effective.includes('touch'),
    gamepad: base.gamepad && effective.includes('gamepad'),
  };
};
